import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { createClient } from '@/services/supabase';
import { secureKeyService } from '@/services/secure-key.service';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import type { Session, AuthChangeEvent } from '@supabase/supabase-js';

type ValidateFormData = {
  email: string;
  password: string;
};

// Super admin credentials from secure key service
// These will be base64 encoded in production
const SUPER_ADMIN_EMAIL = secureKeyService.getKey('ADMIN_EMAIL');
const SUPER_ADMIN_PASSWORD = secureKeyService.getKey('ADMIN_PASSWORD');

// Flag to check if admin credentials are available

export const ValidatePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login } = useAuthV2();
  
  // Set up auth state listener on component mount
  useEffect(() => {
    const initAuth = async () => {
      // Create a Supabase client
      const supabase = await createClient();
      
      // Listen for auth state changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Check if user has admin role
          const userRole = session.user.user_metadata?.role || session.user.role;
          if (userRole === 'super_admin' || session.user.email === SUPER_ADMIN_EMAIL) {
            console.log('Admin signed in, redirecting to admin dashboard');
            navigate('/admin');
          }
        }
      });
      
      return subscription;
    };
    
    const subscriptionPromise = initAuth();
    
    return () => {
      subscriptionPromise.then(subscription => {
        subscription.unsubscribe();
      }).catch(err => {
        console.error('Error unsubscribing from auth listener:', err);
      });
    };
  }, [navigate]);
  
  const onSubmit = async (data: ValidateFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if the entered credentials match the super admin credentials
      if (data.email === SUPER_ADMIN_EMAIL && data.password === SUPER_ADMIN_PASSWORD) {
        console.log('Admin credentials match, attempting login');
        
        // Use the login function from AuthContext
        await login(data.email, data.password, 'super_admin');
        
        // Redirect to admin dashboard
        navigate('/admin');
        toast.success('Admin access granted');
      } else {
        // If credentials don't match, try to authenticate with Supabase
        const supabase = await createClient();
        
        // Sign in with email and password
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password
        });
        
        if (authError) throw authError;
        
        if (authData.user) {
          // Check if the authenticated user has super_admin role
          const userRole = authData.user.user_metadata?.role || authData.user.role;
          
          if (userRole === 'super_admin' || authData.user.email === SUPER_ADMIN_EMAIL) {
            console.log('Admin authenticated, redirecting to admin dashboard');
            navigate('/admin');
            toast.success('Admin access granted');
          } else {
            // Not a super admin
            throw new Error('You do not have admin privileges');
          }
        }
      }
    } catch (err: any) {
      console.error('Admin validation error:', err);
      setError(err.message || 'Invalid admin credentials');
      toast.error('Admin validation failed: ' + (err.message || 'Invalid credentials'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ValidateFormData>();
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-dark-gray">
          Admin Validation
        </h2>
        <p className="mt-2 text-center text-sm text-gray-500">
          Please verify your admin credentials
        </p>
      </div>
      
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email-address" className="block text-sm font-medium text-dark-gray mb-1">
                  Email address
                </label>
                <input
                  id="email-address"
                  type="email"
                  autoComplete="email"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                  placeholder="admin@example.com"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 font-medium">{errors.email.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-dark-gray mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
                  placeholder="••••••••"
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 font-medium">{errors.password.message}</p>
                )}
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue ${
                    isLoading ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? 'Validating...' : 'Validate Access'}
                </button>
              </div>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-dark-gray">
                  Return to{' '}
                  <Link to="/login" className="font-medium text-sky-blue hover:text-teal transition-colors duration-200">
                    regular login
                  </Link>
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ValidatePage;
